/**
 * CloudBackup.js
 * 云端备份和恢复组件
 */

import React, { useState, useEffect } from 'react';
import { Modal, Button, Input, List, Typography, message, Tabs, Upload, Space, Tooltip, Card, Tag, Empty, Progress, Divider, Switch, Popconfirm, Alert } from 'antd';
import { 
  CloudUploadOutlined, 
  CloudDownloadOutlined, 
  InfoCircleOutlined, 
  CopyOutlined, 
  ExportOutlined, 
  ImportOutlined,
  CheckCircleOutlined,
  HistoryOutlined,
  LockOutlined,
  SafetyOutlined,
  FileProtectOutlined,
  ExclamationCircleOutlined,
  FieldTimeOutlined,
  CloseOutlined,
  DeleteOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  UnorderedListOutlined,
  FileTextOutlined,
  FolderOutlined,
  TagsOutlined,
  RollbackOutlined,
  ClockCircleOutlined,
  CloudOutlined,
  KeyOutlined
} from '@ant-design/icons';
import CloudBackupService from '../services/cloudBackupService';
import '../styles/cloudBackup.css';

const { Title, Text, Paragraph } = Typography;
const { Dragger } = Upload;

/**
 * 云端备份和恢复组件
 */
const CloudBackup = ({ visible, onClose, onBackup, onRestore, data }) => {
  const [activeTab, setActiveTab] = useState('backup');
  const [loading, setLoading] = useState(false);
  const [backupHistory, setBackupHistory] = useState([]);
  const [backupId, setBackupId] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [backupMetadata, setBackupMetadata] = useState(null);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [backupSuccess, setBackupSuccess] = useState(false);
  const [currentBackupInfo, setCurrentBackupInfo] = useState(null);
  const [backupProgress, setBackupProgress] = useState(0);
  
  // 新增状态
  const [usePasswordProtection, setUsePasswordProtection] = useState(false);
  const [customPassword, setCustomPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [historyView, setHistoryView] = useState(false); // 是否显示备份历史页面

  // 加载备份历史
  useEffect(() => {
    if (visible) {
      const history = CloudBackupService.getBackupHistory();
      setBackupHistory(history);
      
      // 重置其他状态
      if (!historyView) {
        setActiveTab('backup');
      }
    }
  }, [visible, historyView]);
  
  // 切换到备份历史页面
  const toggleHistoryView = () => {
    setHistoryView(!historyView);
  };

  // 切换密码保护状态
  const handlePasswordProtectionChange = (checked) => {
    setUsePasswordProtection(checked);
    if (!checked) {
      setCustomPassword('');
    }
  };
  
  // 切换密码可见性
  const handlePasswordVisibilityToggle = () => {
    setShowPassword(!showPassword);
  };


  // 创建云端备份
  const handleCreateBackup = async () => {
    try {
      // 如果启用了密码保护但没有输入密码，提示用户
      if (usePasswordProtection && !customPassword) {
        message.warning('请输入备份密码');
        return;
      }
      
      setLoading(true);
      setBackupProgress(0);
      
      // 模拟进度
      const progressInterval = setInterval(() => {
        setBackupProgress(prev => {
          const newProgress = prev + Math.floor(Math.random() * 10);
          return newProgress >= 90 ? 90 : newProgress;
        });
      }, 300);
      
      // 准备备份数据，包含密码保护选项
      const backupData = {
        ...data,
        password: usePasswordProtection ? customPassword : undefined,
        hasPasswordProtection: usePasswordProtection
      };
      
      const backupInfo = await CloudBackupService.createCloudBackup(backupData);
      
      clearInterval(progressInterval);
      setBackupProgress(100);
      
      setTimeout(() => {
        // 将密码保护状态添加到备份信息中
        const enhancedBackupInfo = {
          ...backupInfo,
          hasPasswordProtection: usePasswordProtection
        };
        
        setCurrentBackupInfo(enhancedBackupInfo);
        setBackupSuccess(true);
        
        // 刷新备份历史
        const history = CloudBackupService.getBackupHistory();
        setBackupHistory(history);
        
        message.success('备份创建成功！');
      }, 500);
    } catch (error) {
      message.error('备份创建失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // 导出备份信息
  const handleExportBackupInfo = (backupInfo) => {
    try {
      CloudBackupService.exportBackupInfo(backupInfo);
      message.success('备份信息已导出');
    } catch (error) {
      message.error('导出备份信息失败: ' + error.message);
    }
  };

  // 删除备份历史记录
  const handleDeleteBackupHistory = (backupId) => {
    Modal.confirm({
      title: '确认删除',
      icon: <ExclamationCircleOutlined />,
      content: '确定要删除此备份历史记录吗？此操作不会删除云端实际备份数据，只会从本地历史记录中移除。',
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: () => {
        const success = CloudBackupService.deleteBackupHistory(backupId);
        if (success) {
          message.success('备份历史记录已删除');
          // 更新备份历史列表
          const history = CloudBackupService.getBackupHistory();
          setBackupHistory(history);
        } else {
          message.error('删除备份历史记录失败');
        }
      }
    });
  };

  // 复制备份ID到剪贴板
  const handleCopyBackupId = (id) => {
    navigator.clipboard.writeText(id).then(() => {
      message.success('备份ID已复制到剪贴板');
    }).catch(() => {
      message.error('复制失败，请手动复制');
    });
  };
  
  // 复制验证码到剪贴板
  const handleCopyVerificationCode = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      message.success('验证码已复制到剪贴板');
    }).catch(() => {
      message.error('复制失败，请手动复制');
    });
  };

  // 验证备份ID
  const handleVerifyBackupId = async () => {
    if (!backupId) {
      message.warning('请输入备份ID');
      return;
    }

    try {
      setVerifyLoading(true);
      const metadata = await CloudBackupService.getBackupMetadata(backupId);
      setBackupMetadata(metadata);
    } catch (error) {
      message.error('验证备份ID失败: ' + error.message);
      setBackupMetadata(null);
    } finally {
      setVerifyLoading(false);
    }
  };

  // 恢复备份
  const handleRestoreBackup = async () => {
    if (!backupId || !verificationCode) {
      message.warning('请输入备份ID和验证码');
      return;
    }
    
    // 检查密码保护
    if (backupMetadata && backupMetadata.hasPasswordProtection && !customPassword) {
      message.warning('此备份已启用密码保护，请输入密码');
      return;
    }

    try {
      setRestoreLoading(true);
      // 传递密码到恢复函数
      const restoredData = await CloudBackupService.verifyAndRestoreBackup(
        backupId, 
        verificationCode, 
        backupMetadata && backupMetadata.hasPasswordProtection ? customPassword : undefined
      );
      onRestore(restoredData);
      message.success('备份恢复成功！');
      onClose();
    } catch (error) {
      if (error.message.includes('password')) {
        message.error('密码错误，无法恢复备份');
      } else {
        message.error('恢复备份失败: ' + error.message);
      }
    } finally {
      setRestoreLoading(false);
    }
  };

  // 处理文件导入
  const handleFileImport = async (file) => {
    try {
      const backupInfo = await CloudBackupService.importBackupInfo(file);
      setBackupId(backupInfo.backupId);
      setVerificationCode(backupInfo.verificationCode);
      
      // 自动验证备份ID
      try {
        setVerifyLoading(true);
        const metadata = await CloudBackupService.getBackupMetadata(backupInfo.backupId);
        setBackupMetadata(metadata);
      } catch (error) {
        message.error('验证备份ID失败: ' + error.message);
        setBackupMetadata(null);
      } finally {
        setVerifyLoading(false);
      }
      
      message.success('备份信息导入成功');
    } catch (error) {
      message.error('导入备份信息失败: ' + error.message);
    }
    
    return false; // 阻止默认上传行为
  };

  // 渲染备份成功界面
  const renderBackupSuccess = () => (
    <div className="scale-in backup-success-container" style={{ 
      textAlign: 'center', 
      padding: '10px 0',
      maxWidth: '600px',
      margin: '0 auto'
    }}>
      <div className="backup-success-header" style={{ marginBottom: '20px' }}>
        <div className="success-icon-wrapper bounce-in" style={{ marginBottom: '15px' }}>
          <CheckCircleOutlined className="success-icon" style={{ fontSize: '48px', color: '#52c41a' }} />
          <div className="success-icon-ripple"></div>
        </div>
        <Title level={3} className="success-title fade-in-up" style={{ animationDelay: '0.2s', marginBottom: '5px', fontSize: '22px' }}>备份创建成功！</Title>
        <Text type="secondary" className="success-subtitle fade-in-up" style={{ animationDelay: '0.3s', display: 'block', fontSize: '14px' }}>
          您的数据已安全备份到云端
          <span className="success-emoji" role="img" aria-label="success">✨</span>
        </Text>
      </div>
      
      <Card 
        className="backup-card backup-success-card scale-in" 
        style={{ 
          marginTop: 15, 
          textAlign: 'left', 
          animationDelay: '0.4s',
          borderRadius: '16px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.03)',
          overflow: 'hidden',
          background: '#f9fff9'
        }}
        bordered={false}
        size="small"
      >
        <div className="backup-stats" style={{ 
          display: 'flex', 
          justifyContent: 'space-around', 
          padding: '15px',
          background: 'rgba(255, 255, 255, 0.8)',
          borderRadius: '12px',
          margin: '0 5px'
        }}>
          <div className="backup-stat-item fade-in-right" style={{ 
            animationDelay: '0.5s', 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            <div className="backup-stat-icon" style={{ 
              color: '#1890ff', 
              backgroundColor: 'rgba(24, 144, 255, 0.1)',
              padding: '10px',
              borderRadius: '8px',
              marginBottom: '8px',
              fontSize: '18px'
            }}>
              <FileTextOutlined />
            </div>
            <div className="backup-stat-value" style={{ 
              fontSize: '24px', 
              fontWeight: 'bold', 
              color: '#1890ff'
            }}>
              {currentBackupInfo.metadata.promptCount}
            </div>
            <div className="backup-stat-label" style={{ 
              fontSize: '12px', 
              color: '#8c8c8c'
            }}>
              提示词
            </div>
          </div>
          
          <div className="backup-stat-item fade-in-right" style={{ 
            animationDelay: '0.6s', 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            <div className="backup-stat-icon" style={{ 
              color: '#52c41a', 
              backgroundColor: 'rgba(82, 196, 26, 0.1)',
              padding: '10px',
              borderRadius: '8px',
              marginBottom: '8px',
              fontSize: '18px'
            }}>
              <FolderOutlined />
            </div>
            <div className="backup-stat-value" style={{ 
              fontSize: '24px', 
              fontWeight: 'bold', 
              color: '#52c41a'
            }}>
              {currentBackupInfo.metadata.folderCount || 0}
            </div>
            <div className="backup-stat-label" style={{ 
              fontSize: '12px', 
              color: '#8c8c8c'
            }}>
              文件夹
            </div>
          </div>
          
          <div className="backup-stat-item fade-in-right" style={{ 
            animationDelay: '0.7s', 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            <div className="backup-stat-icon" style={{ 
              color: '#722ed1', 
              backgroundColor: 'rgba(114, 46, 209, 0.1)',
              padding: '10px',
              borderRadius: '8px',
              marginBottom: '8px',
              fontSize: '18px'
            }}>
              <TagsOutlined />
            </div>
            <div className="backup-stat-value" style={{ 
              fontSize: '24px', 
              fontWeight: 'bold', 
              color: '#722ed1'
            }}>
              {currentBackupInfo.metadata.tagCount}
            </div>
            <div className="backup-stat-label" style={{ 
              fontSize: '12px', 
              color: '#8c8c8c'
            }}>
              标签
            </div>
          </div>
        </div>
        
        <div className="backup-success-info-container" style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '15px',
          margin: '15px 5px 5px 5px'
        }}>
          {/* 备份ID部分 */}
          <div className="backup-info-item backup-id-section fade-in-up" style={{ 
            animationDelay: '0.8s',
            background: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '12px',
            padding: '15px',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.02)'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              marginBottom: '10px'
            }}>
              <div style={{ 
                backgroundColor: 'rgba(24, 144, 255, 0.1)', 
                color: '#1890ff',
                borderRadius: '8px',
                width: '32px',
                height: '32px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginRight: '10px'
              }}>
                <FileProtectOutlined style={{ fontSize: '16px' }} />
              </div>
              <Text strong style={{ fontSize: '14px' }}>备份ID</Text>
              <Tag color="blue" className="scale-in" style={{ 
                marginLeft: '8px', 
                fontSize: '12px', 
                padding: '0 4px'
              }}>
                重要
              </Tag>
            </div>
            <div style={{ display: 'flex' }}>
              <Input 
                readOnly
                value={currentBackupInfo.backupId}
                style={{ 
                  flex: 1, 
                  borderTopRightRadius: 0, 
                  borderBottomRightRadius: 0,
                  borderColor: '#d9d9d9'
                }}
              />
              <Button 
                type="primary" 
                icon={<CopyOutlined />} 
                onClick={() => handleCopyBackupId(currentBackupInfo.backupId)}
                style={{ 
                  borderTopLeftRadius: 0, 
                  borderBottomLeftRadius: 0
                }}
              >
                复制
              </Button>
            </div>
          </div>
          
          {/* 验证码部分 */}
          <div className="backup-info-item verification-code-section fade-in-up" style={{ 
            animationDelay: '0.9s',
            background: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '12px',
            padding: '15px',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.02)'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              marginBottom: '10px'
            }}>
              <div style={{ 
                backgroundColor: 'rgba(255, 77, 79, 0.1)', 
                color: '#ff4d4f',
                borderRadius: '8px',
                width: '32px',
                height: '32px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginRight: '10px'
              }}>
                <SafetyOutlined style={{ fontSize: '16px' }} />
              </div>
              <Text strong style={{ fontSize: '14px' }}>验证码</Text>
              <Tag color="error" className="scale-in" style={{ 
                marginLeft: '8px', 
                fontSize: '12px', 
                padding: '0 4px'
              }}>
                必存
              </Tag>
            </div>
            <div style={{ display: 'flex' }}>
              <Input 
                readOnly
                value={currentBackupInfo.verificationCode}
                style={{ 
                  flex: 1, 
                  borderTopRightRadius: 0, 
                  borderBottomRightRadius: 0,
                  borderColor: '#d9d9d9'
                }}
              />
              <Button 
                danger
                icon={<CopyOutlined />} 
                onClick={() => handleCopyVerificationCode(currentBackupInfo.verificationCode)}
                style={{ 
                  borderTopLeftRadius: 0, 
                  borderBottomLeftRadius: 0
                }}
              >
                复制
              </Button>
            </div>
          </div>
          
          <div className="backup-info-tip fade-in-up" style={{ 
            animationDelay: '1.0s',
            background: 'rgba(24, 144, 255, 0.05)',
            borderRadius: '12px',
            padding: '10px 15px',
            display: 'flex',
            alignItems: 'center',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.02)',
            marginBottom: '15px'
          }}>
            <InfoCircleOutlined style={{ 
              color: '#1890ff', 
              fontSize: '16px',
              marginRight: '10px'
            }} />
            <Text style={{ fontSize: '13px' }}>
              备份ID和验证码是恢复数据的唯一凭证，请妥善保存在安全的地方
            </Text>
          </div>
          
          <div className="backup-additional-info" style={{ 
            display: 'flex', 
            gap: '15px', 
            marginBottom: '15px'
          }}>
            {/* 显示密码保护状态 */}
            {currentBackupInfo.hasPasswordProtection && (
              <div className="backup-info-item password-protection-status fade-in-up" style={{ 
                animationDelay: '1s', 
                flex: 1,
                background: 'rgba(255, 255, 255, 0.9)',
                padding: '15px',
                borderRadius: '12px',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.02)'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  marginBottom: '10px'
                }}>
                  <div style={{ 
                    backgroundColor: 'rgba(250, 173, 20, 0.1)', 
                    color: '#faad14',
                    borderRadius: '8px',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginRight: '10px'
                  }}>
                    <LockOutlined style={{ fontSize: '16px' }} />
                  </div>
                  <Text strong style={{ fontSize: '14px' }}>密码保护</Text>
                  <Tag color="warning" className="scale-in" style={{ 
                    marginLeft: '8px', 
                    fontSize: '12px', 
                    padding: '0 4px'
                  }}>安全</Tag>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Tag color="warning" icon={<LockOutlined />} style={{ margin: 0, fontSize: '12px' }}>已启用</Tag>
                  <Tag color="blue" icon={<CheckCircleOutlined />} style={{ margin: 0, fontSize: '12px' }}>安全加密</Tag>
                </div>
              </div>
            )}
            
            <div className="backup-info-item time-section fade-in-up" style={{ 
              animationDelay: '1.1s', 
              flex: 1,
              background: 'rgba(255, 255, 255, 0.9)',
              padding: '15px',
              borderRadius: '12px',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.02)'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '10px'
              }}>
                <div style={{ 
                  backgroundColor: 'rgba(114, 46, 209, 0.1)', 
                  color: '#722ed1',
                  borderRadius: '8px',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginRight: '10px'
                }}>
                  <FieldTimeOutlined style={{ fontSize: '16px' }} />
                </div>
                <Text strong style={{ fontSize: '14px' }}>创建时间</Text>
              </div>
              <div>
                <Text style={{ fontSize: '13px', display: 'flex', alignItems: 'center' }}>
                  <ClockCircleOutlined style={{ marginRight: '5px', color: '#722ed1' }} />
                  {new Date(currentBackupInfo.createdAt).toLocaleString()}
                </Text>
              </div>
            </div>
          </div>
        </div>
        
        {currentBackupInfo.metadata.samplePrompts && currentBackupInfo.metadata.samplePrompts.length > 0 && (
          <div className="sample-prompts-container fade-in-up" style={{ 
            animationDelay: '1.2s',
            background: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '12px',
            padding: '15px',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.02)',
            marginTop: '5px',
            marginBottom: '15px'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              marginBottom: '12px'
            }}>
              <div style={{ 
                backgroundColor: 'rgba(24, 144, 255, 0.1)', 
                color: '#1890ff',
                borderRadius: '8px',
                width: '32px',
                height: '32px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginRight: '10px'
              }}>
                <TagsOutlined style={{ fontSize: '16px' }} />
              </div>
              <Text strong style={{ fontSize: '14px' }}>提示词示例</Text>
              <Tag color="blue" className="scale-in" style={{ 
                marginLeft: '8px', 
                fontSize: '12px', 
                padding: '0 4px'
              }}>预览</Tag>
            </div>
            
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {currentBackupInfo.metadata.samplePrompts.slice(0, 5).map((title, index) => (
                <Tag 
                  key={index} 
                  color="blue" 
                  className="fade-in-right" 
                  style={{ 
                    margin: 0,
                    animationDelay: `${1.3 + index * 0.1}s`,
                    borderRadius: '4px',
                    padding: '4px 8px',
                    fontSize: '12px'
                  }}
                >
                  <FileTextOutlined style={{ marginRight: '4px' }} />
                  {title.length > 15 ? title.substring(0, 15) + '...' : title}
                </Tag>
              ))}
              {currentBackupInfo.metadata.samplePrompts.length > 5 && (
                <Tag style={{ 
                  margin: 0, 
                  background: '#f0f0f0', 
                  borderRadius: '4px', 
                  padding: '4px 8px', 
                  fontSize: '12px' 
                }}>
                  +{currentBackupInfo.metadata.samplePrompts.length - 5} 个
                </Tag>
              )}
            </div>
          </div>
        )}
      </Card>
      
      <div className="backup-tip fade-in-up" style={{ 
        animationDelay: '1.4s', 
        marginTop: '20px',
        marginBottom: '20px',
        background: 'rgba(255, 249, 249, 0.95)',
        borderRadius: '12px',
        padding: '15px',
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.02)',
        border: '1px solid rgba(255, 77, 79, 0.15)'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'flex-start', 
          marginBottom: '10px'
        }}>
          <div style={{ 
            backgroundColor: 'rgba(255, 77, 79, 0.1)', 
            color: '#ff4d4f',
            borderRadius: '8px',
            width: '32px',
            height: '32px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginRight: '12px',
            marginTop: '2px'
          }}>
            <ExclamationCircleOutlined style={{ fontSize: '18px' }} />
          </div>
          <div>
            <Text strong style={{ fontSize: '15px', color: '#ff4d4f', display: 'block', marginBottom: '8px' }}>
              重要提示
            </Text>
            <Text style={{ fontSize: '13px', lineHeight: '1.5', display: 'block', color: '#333' }}>
              请妥善保存备份ID和验证码，它们是恢复数据的必要凭证。建议点击下方“导出备份信息”按钮将信息保存到本地文件。
            </Text>
            {currentBackupInfo.hasPasswordProtection && (
              <div style={{ 
                marginTop: '10px', 
                padding: '8px 12px', 
                background: 'rgba(250, 173, 20, 0.08)', 
                borderRadius: '6px',
                borderLeft: '3px solid #faad14',
                display: 'flex',
                alignItems: 'center'
              }}>
                <LockOutlined style={{ color: '#faad14', marginRight: '8px' }} />
                <Text style={{ fontSize: '13px', color: '#d48806' }}>
                  此备份已启用密码保护，恢复时需要输入您设置的密码。
                </Text>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="backup-success-actions fade-in-up" style={{ 
        marginTop: 20, 
        marginBottom: 20,
        animationDelay: '1.5s', 
        display: 'flex', 
        gap: '15px'
      }}>
        <Button 
          type="primary" 
          icon={<ExportOutlined />} 
          onClick={() => handleExportBackupInfo(currentBackupInfo)}
          size="large"
          style={{ 
            flex: 1, 
            height: '45px',
            borderRadius: '8px',
            fontWeight: 500
          }}
        >
          导出备份信息
        </Button>
        <Button 
          onClick={() => setBackupSuccess(false)}
          icon={<RollbackOutlined />}
          size="large"
          style={{ 
            flex: 1, 
            height: '45px',
            borderRadius: '8px'
          }}
        >
          返回备份页面
        </Button>
      </div>
    </div>
  );

  // 已删除未使用的renderBackupHistory函数


  // 渲染备份选项卡
  const renderBackupTab = () => {
    // 如果当前在备份历史页面，则显示备份历史
    if (historyView) {
      return (
        <div className="history-view-container fade-in-up">
          <div className="backup-nav-header">
            <div className="backup-nav-title">
              <div className="history-title-icon">
                <HistoryOutlined />
              </div>
              <span>备份历史</span>
            </div>
            <Button 
              icon={<RollbackOutlined />} 
              onClick={toggleHistoryView}
              size="middle"
              className="history-back-button"
            >
              返回备份页面
            </Button>
          </div>
          
          {backupHistory.length > 0 ? (
            <div className="backup-history-list">
              <div className="backup-history-header-main fade-in-up" style={{ animationDelay: '0.1s' }}>
                <Title level={4} className="backup-history-title-main">
                  <div className="history-icon-wrapper">
                    <HistoryOutlined className="history-icon" />
                  </div>
                  备份历史记录
                </Title>
                <Text type="secondary" className="backup-history-description">
                  您可以查看、恢复或删除您的备份记录，保持数据安全与可恢复性。
                  每条备份记录包含您的提示词、文件夹和标签等重要信息。
                </Text>
                <div className="backup-history-stats-container scale-in" style={{ 
                  animationDelay: '0.3s',
                  display: 'flex',
                  justifyContent: 'space-around',
                  background: 'linear-gradient(135deg, #f0f7ff, #e6f7ff)',
                  borderRadius: '16px',
                  padding: '20px',
                  marginTop: '20px',
                  boxShadow: '0 4px 12px rgba(24, 144, 255, 0.1)'
                }}>
                  <div className="backup-history-stat" style={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    flex: 1,
                    padding: '10px 0'
                  }}>
                    <div className="stat-icon prompt-icon" style={{
                      background: 'rgba(24, 144, 255, 0.15)',
                      borderRadius: '50%',
                      width: '48px',
                      height: '48px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      marginBottom: '12px'
                    }}>
                      <CloudOutlined style={{ color: '#1890ff', fontSize: '24px' }} />
                    </div>
                    <div className="stat-content" style={{ textAlign: 'center' }}>
                      <div className="stat-label" style={{ fontSize: '14px', color: '#8c8c8c', marginBottom: '6px' }}>备份记录</div>
                      <div className="stat-value" style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>{backupHistory.length}</div>
                    </div>
                  </div>
                  <div className="backup-history-stat" style={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    flex: 1,
                    padding: '10px 0',
                    borderLeft: '1px dashed #bae7ff',
                    borderRight: '1px dashed #bae7ff'
                  }}>
                    <div className="stat-icon folder-icon" style={{
                      background: 'rgba(82, 196, 26, 0.15)',
                      borderRadius: '50%',
                      width: '48px',
                      height: '48px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      marginBottom: '12px'
                    }}>
                      <FolderOutlined style={{ color: '#52c41a', fontSize: '24px' }} />
                    </div>
                    <div className="stat-content" style={{ textAlign: 'center' }}>
                      <div className="stat-label" style={{ fontSize: '14px', color: '#8c8c8c', marginBottom: '6px' }}>文件夹数</div>
                      <div className="stat-value" style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>{backupHistory.reduce((total, item) => {
                        return total + (item.metadata?.folderCount || 0);
                      }, 0)}</div>
                    </div>
                  </div>
                  <div className="backup-history-stat" style={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    flex: 1,
                    padding: '10px 0'
                  }}>
                    <div className="stat-icon tag-icon" style={{
                      background: 'rgba(250, 173, 20, 0.15)',
                      borderRadius: '50%',
                      width: '48px',
                      height: '48px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      marginBottom: '12px'
                    }}>
                      <LockOutlined style={{ color: '#faad14', fontSize: '24px' }} />
                    </div>
                    <div className="stat-content" style={{ textAlign: 'center' }}>
                      <div className="stat-label" style={{ fontSize: '14px', color: '#8c8c8c', marginBottom: '6px' }}>已加密</div>
                      <div className="stat-value" style={{ fontSize: '24px', fontWeight: 'bold', color: '#faad14' }}>{backupHistory.filter(item => item.hasPasswordProtection).length}</div>
                    </div>
                  </div>
                </div>
              </div>
              
              <List
                itemLayout="vertical"
                dataSource={backupHistory}
                locale={{ 
                  emptyText: (
                    <div className="history-empty-state fade-in-up">
                      <Empty 
                        description={
                          <div>
                            <p className="empty-title" style={{ fontSize: '16px', fontWeight: 500, color: '#262626', marginBottom: '8px' }}>暂无备份记录</p>
                            <p className="empty-subtitle" style={{ fontSize: '14px', color: '#8c8c8c' }}>创建一个新的备份来保护您的数据安全</p>
                          </div>
                        } 
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        style={{
                          background: 'rgba(255, 255, 255, 0.7)',
                          padding: '30px',
                          borderRadius: '16px',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)'
                        }}
                      />
                      <Button 
                        type="primary" 
                        icon={<CloudUploadOutlined />} 
                        onClick={toggleHistoryView}
                        className="create-backup-btn scale-in"
                        style={{ 
                          marginTop: 24, 
                          animationDelay: '0.5s',
                          borderRadius: '12px',
                          height: '40px',
                          boxShadow: '0 4px 12px rgba(24, 144, 255, 0.15)'
                        }}
                      >
                        创建新备份
                      </Button>
                    </div>
                  )
                }}
                renderItem={(item, index) => (
                  <Card 
                    className={`backup-history-item ${index % 2 === 0 ? 'fade-in-left' : 'fade-in-right'}`} 
                    size="small" 
                    hoverable
                    style={{ 
                      marginBottom: 16, 
                      animationDelay: `${0.2 + index * 0.1}s`,
                      borderRadius: 16,
                      overflow: 'hidden',
                      border: item.hasPasswordProtection ? '1px solid #ffe58f' : '1px solid #f0f0f0',
                      boxShadow: item.hasPasswordProtection ? 
                        '0 8px 16px rgba(250, 173, 20, 0.08)' : 
                        '0 8px 16px rgba(0, 0, 0, 0.03)'
                    }}
                  >
                    <List.Item
                      actions={[
                        <Tooltip title="复制备份ID" key="copy">
                          <Button 
                            type="text" 
                            size="small" 
                            onClick={() => handleCopyBackupId(item.id)}
                            icon={<CopyOutlined style={{ fontSize: '16px', color: '#1890ff' }} />}
                            className="history-action-button fade-in-up"
                            style={{ 
                              animationDelay: `${1.0 + index * 0.1}s`,
                              background: 'rgba(24, 144, 255, 0.08)',
                              borderRadius: '8px',
                              width: '32px',
                              height: '32px',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              marginRight: '8px'
                            }}
                          />
                        </Tooltip>,
                        <Tooltip title="导出备份信息" key="export">
                          <Button 
                            type="text" 
                            size="small" 
                            onClick={() => handleExportBackupInfo(item)}
                            icon={<ExportOutlined style={{ fontSize: '16px', color: '#52c41a' }} />}
                            className="history-action-button export-backup-btn fade-in-up"
                            style={{ 
                              animationDelay: `${1.1 + index * 0.1}s`,
                              background: 'rgba(82, 196, 26, 0.08)',
                              borderRadius: '8px',
                              width: '32px',
                              height: '32px',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              marginRight: '8px'
                            }}
                          />
                        </Tooltip>,
                        <Tooltip title="恢复此备份" key="restore">
                          <Button 
                            type="text" 
                            size="small" 
                            onClick={() => handleRestoreBackup(item.id)}
                            icon={<CloudDownloadOutlined style={{ fontSize: '16px', color: '#722ed1' }} />}
                            className="history-action-button restore-backup-btn fade-in-up"
                            style={{ 
                              animationDelay: `${1.15 + index * 0.1}s`,
                              background: 'rgba(114, 46, 209, 0.08)',
                              borderRadius: '8px',
                              width: '32px',
                              height: '32px',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              marginRight: '8px'
                            }}
                          />
                        </Tooltip>,
                        <Popconfirm
                          title="删除备份记录"
                          description="确定要删除此备份历史记录吗？此操作不会删除云端实际备份数据。"
                          onConfirm={() => handleDeleteBackupHistory(item.id)}
                          okText="删除"
                          cancelText="取消"
                          okButtonProps={{ danger: true }}
                          icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}
                          key="delete"
                          overlayClassName="backup-delete-popconfirm"
                        >
                          <Tooltip title="删除记录" key="delete-tooltip">
                            <Button 
                              type="text" 
                              size="small" 
                              danger
                              icon={<DeleteOutlined style={{ fontSize: '16px' }} />}
                              className="history-action-button delete-backup-btn fade-in-up"
                              style={{ 
                                animationDelay: `${1.2 + index * 0.1}s`,
                                background: 'rgba(255, 77, 79, 0.08)',
                                borderRadius: '8px',
                                width: '32px',
                                height: '32px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                              }}
                            />
                          </Tooltip>
                        </Popconfirm>
                      ]}
                    >
                      <List.Item.Meta
                        title={
                          <div className="backup-history-title-container">
                            <div className="backup-id-wrapper">
                              <div className="backup-id-icon">
                                <FileProtectOutlined style={{ color: '#1890ff', fontSize: 18, transition: 'all 0.3s ease' }} />
                              </div>
                              <Text code ellipsis className="backup-id-text">{item.id}</Text>
                              {item.hasPasswordProtection && (
                                <Tag color="warning" icon={<LockOutlined />} className="password-protected-tag bounce-in" style={{ 
                                  animationDelay: `${0.3 + index * 0.1}s`,
                                  borderRadius: '6px',
                                  padding: '0 8px',
                                  marginLeft: '8px',
                                  background: 'linear-gradient(135deg, #fff7e6, #fff1cc)',
                                  border: 'none',
                                  color: '#d48806'
                                }}>
                                  已加密
                                </Tag>
                              )}
                            </div>
                            <div className="backup-date-wrapper">
                              <Tag className="backup-tag date-tag scale-in" style={{ 
                                animationDelay: `${0.4 + index * 0.1}s`,
                                background: 'linear-gradient(135deg, #f0f7ff, #e6f7ff)',
                                borderRadius: '6px',
                                border: 'none',
                                color: '#1890ff',
                                padding: '4px 10px',
                                marginRight: '8px',
                                boxShadow: '0 2px 6px rgba(24, 144, 255, 0.1)'
                              }}>
                                <FieldTimeOutlined style={{ marginRight: 6, fontSize: '14px' }} />
                                {new Date(item.date).toLocaleDateString()}
                              </Tag>
                              <Tag className="backup-tag time-tag scale-in" style={{ 
                                animationDelay: `${0.5 + index * 0.1}s`,
                                background: 'linear-gradient(135deg, #f0f5ff, #d6e4ff)',
                                borderRadius: '6px',
                                border: 'none',
                                color: '#2f54eb',
                                padding: '4px 10px',
                                boxShadow: '0 2px 6px rgba(47, 84, 235, 0.1)'
                              }}>
                                <ClockCircleOutlined style={{ marginRight: 6, fontSize: '14px' }} />
                                {new Date(item.date).toLocaleTimeString()}
                              </Tag>
                            </div>
                          </div>
                        }
                        description={
                          <Space direction="vertical" size={12} style={{ width: '100%' }}>
                            <div className="backup-history-stats-container fade-in-up" style={{ 
                              animationDelay: `${0.6 + index * 0.1}s`,
                              display: 'flex',
                              justifyContent: 'space-between',
                              background: 'linear-gradient(135deg, #f9f9f9, #f5f5f5)',
                              borderRadius: '12px',
                              padding: '12px 16px',
                              marginBottom: '5px'
                            }}>
                              <div className="backup-history-stat" style={{ 
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                flex: 1,
                                padding: '8px 0'
                              }}>
                                <div className="stat-icon prompt-icon" style={{
                                  background: 'rgba(24, 144, 255, 0.1)',
                                  borderRadius: '50%',
                                  width: '36px',
                                  height: '36px',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  marginBottom: '8px'
                                }}>
                                  <FileTextOutlined style={{ color: '#1890ff', fontSize: '18px' }} />
                                </div>
                                <div className="stat-content" style={{ textAlign: 'center' }}>
                                  <div className="stat-label" style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '4px' }}>提示词</div>
                                  <div className="stat-value" style={{ fontSize: '18px', fontWeight: 'bold', color: '#262626' }}>{item.metadata.promptCount}</div>
                                </div>
                              </div>
                              <div className="backup-history-stat" style={{ 
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                flex: 1,
                                padding: '8px 0',
                                borderLeft: '1px dashed #f0f0f0',
                                borderRight: '1px dashed #f0f0f0'
                              }}>
                                <div className="stat-icon folder-icon" style={{
                                  background: 'rgba(82, 196, 26, 0.1)',
                                  borderRadius: '50%',
                                  width: '36px',
                                  height: '36px',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  marginBottom: '8px'
                                }}>
                                  <FolderOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
                                </div>
                                <div className="stat-content" style={{ textAlign: 'center' }}>
                                  <div className="stat-label" style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '4px' }}>文件夹</div>
                                  <div className="stat-value" style={{ fontSize: '18px', fontWeight: 'bold', color: '#262626' }}>{item.metadata.folderCount || 0}</div>
                                </div>
                              </div>
                              <div className="backup-history-stat" style={{ 
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                flex: 1,
                                padding: '8px 0'
                              }}>
                                <div className="stat-icon tag-icon" style={{
                                  background: 'rgba(250, 173, 20, 0.1)',
                                  borderRadius: '50%',
                                  width: '36px',
                                  height: '36px',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  marginBottom: '8px'
                                }}>
                                  <TagsOutlined style={{ color: '#faad14', fontSize: '18px' }} />
                                </div>
                                <div className="stat-content" style={{ textAlign: 'center' }}>
                                  <div className="stat-label" style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '4px' }}>标签</div>
                                  <div className="stat-value" style={{ fontSize: '18px', fontWeight: 'bold', color: '#262626' }}>{item.metadata.tagCount}</div>
                                </div>
                              </div>
                            </div>
                            {item.metadata.samplePrompts && item.metadata.samplePrompts.length > 0 && (
                              <div className="sample-prompts-preview fade-in-up" style={{ 
                                marginTop: 12, 
                                animationDelay: `${0.7 + index * 0.1}s`,
                                background: 'linear-gradient(135deg, #f9f9f9, #f5f5f5)',
                                borderRadius: '10px',
                                padding: '10px',
                                border: '1px dashed #f0f0f0'
                              }}>
                                <div className="sample-prompts-label" style={{ marginBottom: '8px' }}>
                                  <FileTextOutlined style={{ marginRight: 6, color: '#1890ff', fontSize: '14px' }} />
                                  <Text type="secondary" style={{ fontSize: '13px' }}>示例提示词:</Text>
                                </div>
                                <div className="sample-prompts-tags">
                                  {item.metadata.samplePrompts.slice(0, 2).map((title, index) => (
                                    <Tag 
                                      key={index} 
                                      className="sample-prompt-tag scale-in" 
                                      style={{ 
                                        margin: '0 8px 8px 0',
                                        animationDelay: `${0.8 + index * 0.1}s`,
                                        background: 'linear-gradient(135deg, #e6f7ff, #bae7ff)',
                                        borderRadius: '6px',
                                        border: 'none',
                                        color: '#096dd9',
                                        padding: '4px 10px',
                                        fontSize: '12px',
                                        fontWeight: 500
                                      }}
                                    >
                                      {title}
                                    </Tag>
                                  ))}
                                  {item.metadata.samplePrompts.length > 2 && (
                                    <Tag 
                                      className="more-tag scale-in" 
                                      style={{ 
                                        margin: '0 6px 6px 0',
                                        animationDelay: `${0.9 + index * 0.1}s`,
                                        borderRadius: '6px',
                                        padding: '2px 10px',
                                        background: 'linear-gradient(135deg, #f0f0f0, #f5f5f5)',
                                        border: 'none',
                                        color: '#595959',
                                        fontWeight: 500
                                      }}
                                    >
                                      +{item.metadata.samplePrompts.length - 2}
                                    </Tag>
                                  )}
                                </div>
                              </div>
                            )}
                          </Space>
                        }
                      />
                    </List.Item>
                  </Card>
                )}
              />
            </div>
          ) : (
            <Empty 
              description={
                <span style={{ color: '#8c8c8c', fontSize: '14px' }}>
                  暂无备份历史记录
                </span>
              } 
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              style={{ 
                margin: '60px 0', 
                background: 'rgba(255, 255, 255, 0.7)',
                padding: '30px',
                borderRadius: '16px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)'
              }} 
            />
          )}
        </div>
      );
    }
    
    // 否则显示备份创建页面
    return (
      <div className="slide-in">
        {backupSuccess ? (
          renderBackupSuccess()
        ) : (
          <>
            <Card className="backup-card backup-info-card scale-in">
              <Space align="start">
                <CloudUploadOutlined style={{ fontSize: 20, color: '#1890ff' }} />
                <div>
                  <Text strong style={{ fontSize: 15 }}>创建云端备份</Text>
                  <Paragraph style={{ marginTop: 6 }}>
                    <Text>
                      将您的所有提示词、文件夹和标签保存到云端，以便在清理浏览器缓存或更换设备后恢复数据。
                    </Text>
                  </Paragraph>
                </div>
              </Space>
            </Card>
            
            {/* 密码保护选项 */}
            <div className="password-protection-section fade-in-up" style={{ animationDelay: '0.3s' }}>
              <Card 
                className="password-protection-card"
                size="small"
                bordered={false}
                style={{
                  borderRadius: 12,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                  background: usePasswordProtection ? 'linear-gradient(to right, rgba(230, 244, 255, 0.6), rgba(216, 237, 255, 0.4))' : 'white'
                }}
              >
                <div className="password-protection-header">
                  <div className="protection-icon-wrapper bounce-in" style={{ animationDelay: '0.4s' }}>
                    <LockOutlined className="protection-icon" />
                  </div>
                  <div className="protection-title-wrapper">
                    <Text strong className="protection-title">备份密码保护</Text>
                    <Tag color="blue" className="protection-tag scale-in" style={{ animationDelay: '0.5s' }}>推荐</Tag>
                  </div>
                  <Switch 
                    checked={usePasswordProtection} 
                    onChange={handlePasswordProtectionChange} 
                    checkedChildren="已启用"
                    unCheckedChildren="未启用"
                    className="protection-switch scale-in"
                    style={{ animationDelay: '0.6s' }}
                  />
                </div>
                
                {usePasswordProtection && (
                  <div className="password-input-wrapper fade-in-up" style={{ animationDelay: '0.2s' }}>
                    <div className="password-input-container">
                      <Input.Password
                        placeholder="设置备份密码"
                        value={customPassword}
                        onChange={(e) => setCustomPassword(e.target.value)}
                        visibilityToggle={{
                          visible: showPassword,
                          onVisibleChange: handlePasswordVisibilityToggle,
                        }}
                        className="backup-input password-input"
                        prefix={<LockOutlined style={{ color: '#1890ff' }} />}
                        suffix={
                          showPassword ? 
                            <EyeInvisibleOutlined className="password-toggle-icon" onClick={handlePasswordVisibilityToggle} /> : 
                            <EyeOutlined className="password-toggle-icon" onClick={handlePasswordVisibilityToggle} />
                        }
                      />
                    </div>
                    
                    <div className="password-strength-container fade-in-up" style={{ animationDelay: '0.3s' }}>
                      <div className="password-strength-header">
                        <Text type="secondary" className="strength-label">
                          密码强度：
                          <Text 
                            type={customPassword.length > 8 ? "success" : customPassword.length > 4 ? "warning" : "danger"} 
                            strong 
                            className="strength-value"
                          >
                            {customPassword.length > 8 ? "强" : customPassword.length > 4 ? "中" : "弱"}
                          </Text>
                        </Text>
                      </div>
                      <Progress 
                        percent={customPassword.length > 0 ? Math.min(100, customPassword.length * 10) : 0} 
                        size="small" 
                        status={customPassword.length > 8 ? "success" : customPassword.length > 4 ? "normal" : "exception"}
                        showInfo={false}
                        className="password-strength-progress"
                        strokeColor={{
                          '0%': customPassword.length > 8 ? '#52c41a' : customPassword.length > 4 ? '#faad14' : '#ff4d4f',
                          '100%': customPassword.length > 8 ? '#52c41a' : customPassword.length > 4 ? '#faad14' : '#ff4d4f',
                        }}
                      />
                    </div>
                    
                    <Alert
                      className="password-tip-alert fade-in-up"
                      style={{ animationDelay: '0.4s', marginTop: 12 }}
                      message={
                        <div className="password-tip-container">
                          <InfoCircleOutlined className="tip-icon" />
                          <Text type="secondary" className="password-tip">
                            添加密码可增强备份安全性，恢复时需要提供此密码，请妥善保管
                          </Text>
                        </div>
                      }
                      type="info"
                      showIcon={false}
                    />
                  </div>
                )}
              </Card>
            </div>
            
            {loading ? (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <Progress type="circle" percent={backupProgress} size={80} />
                <div style={{ marginTop: 16 }}>
                  <Text>正在创建备份，请稍候...</Text>
                </div>
              </div>
            ) : (
              <Button
                type="primary"
                icon={<CloudUploadOutlined />}
                onClick={handleCreateBackup}
                className="primary-gradient-btn"
                style={{ width: '100%', height: 44, marginTop: 16, marginBottom: 20 }}
              >
                创建云端备份
              </Button>
            )}
            
            {/* 备份历史导航按钮 */}
            <div className="backup-history-card">
              <div className="backup-history-header">
                <div className="backup-history-title">
                  <div className="history-icon-small">
                    <HistoryOutlined />
                  </div>
                  <span>备份历史</span>
                </div>
                <Button 
                  type="primary" 
                  icon={<UnorderedListOutlined />} 
                  onClick={toggleHistoryView}
                  size="small"
                  className="view-all-button"
                >
                  查看全部
                </Button>
              </div>
              
              {backupHistory.length > 0 ? (
                <div className="history-preview-container">
                  <List
                    size="small"
                    dataSource={backupHistory.slice(0, 2)}
                    renderItem={(item, index) => (
                      <List.Item
                        className={`history-preview-item fade-in-up`}
                        style={{ animationDelay: `${index * 0.1}s` }}
                        actions={[
                          <Button 
                            type="link" 
                            size="small" 
                            onClick={() => handleExportBackupInfo(item)}
                            icon={<ExportOutlined />}
                            className="preview-action-button"
                          >
                            导出
                          </Button>
                        ]}
                      >
                        <List.Item.Meta
                          title={
                            <div className="preview-item-title">
                              <div className="preview-id-container">
                                <div className="preview-icon-wrapper">
                                  <FileProtectOutlined className="preview-id-icon" />
                                </div>
                                <Text code ellipsis className="preview-id-text">{item.id}</Text>
                              </div>
                              <div className="preview-tags">
                                <Tag color="blue" size="small" className="preview-date-tag">
                                  <FieldTimeOutlined style={{ marginRight: 4 }} />
                                  {new Date(item.date).toLocaleDateString()}
                                </Tag>
                                {item.hasPasswordProtection && (
                                  <Tag color="warning" size="small" className="preview-lock-tag">
                                    <LockOutlined />
                                  </Tag>
                                )}
                              </div>
                            </div>
                          }
                          description={
                            <div className="preview-stats">
                              <div className="preview-stat-item">
                                <div className="preview-stat-icon">
                                  <FileTextOutlined />
                                </div>
                                <Text type="secondary" className="preview-stat">
                                  {item.metadata.promptCount} 提示词
                                </Text>
                              </div>
                              <div className="preview-stat-item">
                                <div className="preview-stat-icon">
                                  <FolderOutlined />
                                </div>
                                <Text type="secondary" className="preview-stat">
                                  {item.metadata.folderCount || 0} 文件夹
                                </Text>
                              </div>
                              <div className="preview-stat-item">
                                <div className="preview-stat-icon">
                                  <TagsOutlined />
                                </div>
                                <Text type="secondary" className="preview-stat">
                                  {item.metadata.tagCount} 标签
                                </Text>
                              </div>
                            </div>
                          }
                        />
                      </List.Item>
                    )}
                  />
                  
                  {backupHistory.length > 2 && (
                    <div className="view-more-container">
                      <Button 
                        type="link" 
                        onClick={toggleHistoryView} 
                        size="small"
                        className="view-more-button"
                        icon={<UnorderedListOutlined />}
                      >
                        查看更多 ({backupHistory.length - 2})
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <Empty 
                  description="暂无备份历史记录" 
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  className="history-empty-state" 
                />
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  // 渲染恢复选项卡
  const renderRestoreTab = () => (
    <div className="slide-in">
      <Card className="backup-card backup-info-card scale-in" style={{ marginBottom: 20 }}>
        <Space align="start">
          <CloudDownloadOutlined style={{ fontSize: 20, color: '#1890ff' }} />
          <div>
            <Text strong style={{ fontSize: 15 }}>从云端恢复数据</Text>
            <Paragraph style={{ marginTop: 6 }}>
              <Text>
                请输入您之前创建的备份ID和验证码，或者导入备份信息文件，以恢复您的数据。
              </Text>
            </Paragraph>
          </div>
        </Space>
      </Card>
      
      <div style={{ marginBottom: 20 }}>
        <Dragger
          name="file"
          multiple={false}
          beforeUpload={handleFileImport}
          showUploadList={false}
          accept=".json"
          className="backup-upload-area"
          style={{ background: 'transparent' }}
        >
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <ImportOutlined className="backup-upload-icon" />
            <Title level={5}>点击或拖拽文件到此区域导入备份信息</Title>
            <Text type="secondary">支持导入之前导出的备份信息文件</Text>
          </div>
        </Dragger>
      </div>
      
      <Divider>
        <Text type="secondary">或者手动输入备份信息</Text>
      </Divider>
      
      <div style={{ marginBottom: 16 }}>
        <Text strong>备份ID：</Text>
        <Input
          placeholder="输入备份ID"
          value={backupId}
          onChange={e => setBackupId(e.target.value)}
          className="backup-input"
          style={{ marginTop: 8 }}
          prefix={<LockOutlined />}
          suffix={
            <Tooltip title="备份ID是创建备份时生成的唯一标识符">
              <InfoCircleOutlined />
            </Tooltip>
          }
          allowClear
        />
        
        <Button
          type="primary"
          onClick={handleVerifyBackupId}
          loading={verifyLoading}
          disabled={!backupId}
          style={{ marginTop: 12, width: '100%', height: 44 }}
          className="primary-gradient-btn"
          icon={<SafetyOutlined />}
        >
          验证备份ID
        </Button>
      </div>
      
      {backupMetadata && (
        <Card className="backup-card scale-in" size="small" style={{ marginBottom: 16 }}>
          <Space align="center" style={{ marginBottom: 8 }}>
            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
            <Text strong>备份信息验证成功</Text>
          </Space>
          
          <div className="backup-stats" style={{ margin: '12px 0' }}>
            <div className="backup-stat-item">
              <div className="backup-stat-value">{backupMetadata.metadata.promptCount}</div>
              <div className="backup-stat-label">提示词</div>
            </div>
            <div className="backup-stat-item">
              <div className="backup-stat-value">{backupMetadata.metadata.folderCount}</div>
              <div className="backup-stat-label">文件夹</div>
            </div>
            <div className="backup-stat-item">
              <div className="backup-stat-value">{backupMetadata.metadata.tagCount}</div>
              <div className="backup-stat-label">标签</div>
            </div>
          </div>
          
          <Paragraph>
            <Space align="start">
              <FieldTimeOutlined style={{ color: '#722ed1' }} />
              <div>
                <Text strong>创建时间：</Text>
                <Text>{new Date(backupMetadata.createdAt).toLocaleString()}</Text>
              </div>
            </Space>
          </Paragraph>
          
          {backupMetadata.metadata.samplePrompts && backupMetadata.metadata.samplePrompts.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <Text strong>示例提示词：</Text>
              <div style={{ marginTop: 4 }}>
                {backupMetadata.metadata.samplePrompts.slice(0, 3).map((title, index) => (
                  <Tag key={index} color="blue" className="backup-tag" style={{ margin: '0 4px 4px 0' }}>{title}</Tag>
                ))}
                {backupMetadata.metadata.samplePrompts.length > 3 && (
                  <Tag color="blue" className="backup-tag">+{backupMetadata.metadata.samplePrompts.length - 3}</Tag>
                )}
              </div>
            </div>
          )}
        </Card>
      )}
      
      <div>
        <Text strong>验证码：</Text>
        <Input
          placeholder="输入验证码"
          value={verificationCode}
          onChange={e => setVerificationCode(e.target.value)}
          className="backup-input"
          style={{ marginTop: 8 }}
          prefix={<SafetyOutlined />}
          suffix={
            <Tooltip title="验证码是创建备份时生成的，用于验证您的身份">
              <InfoCircleOutlined />
            </Tooltip>
          }
          allowClear
        />
        
        {/* 无论备份是否需要密码，都显示密码输入框，但根据状态有不同的样式和提示 */}
        <div style={{ marginTop: 16 }}>
          <Space align="center" style={{ marginBottom: 8 }}>
            <KeyOutlined style={{ color: '#722ed1' }} />
            <Text strong>备份密码：</Text>
            {backupMetadata && backupMetadata.hasPasswordProtection && (
              <Tag color="warning" className="password-required-tag pulse-animation" style={{ marginLeft: 4 }}>
                <LockOutlined /> 必填
              </Tag>
            )}
            {backupMetadata && !backupMetadata.hasPasswordProtection && (
              <Tag color="success" className="password-optional-tag scale-in" style={{ marginLeft: 4 }}>
                <CheckCircleOutlined /> 无需密码
              </Tag>
            )}
            {!backupMetadata && (
              <Tag color="default" className="password-unknown-tag" style={{ marginLeft: 4 }}>
                <InfoCircleOutlined /> 验证备份ID后确认
              </Tag>
            )}
          </Space>
          <Input.Password
            placeholder={backupMetadata && backupMetadata.hasPasswordProtection ? "输入备份密码（必填）" : "输入备份密码（如有）"}
            value={customPassword}
            onChange={e => setCustomPassword(e.target.value)}
            className="backup-input"
            disabled={backupMetadata && !backupMetadata.hasPasswordProtection}
            prefix={<LockOutlined style={{ color: backupMetadata && backupMetadata.hasPasswordProtection ? '#faad14' : '#d9d9d9' }} />}
            suffix={
              <Tooltip title={backupMetadata && backupMetadata.hasPasswordProtection ? 
                "此备份已启用密码保护，请输入创建备份时设置的密码" : 
                "如果此备份设置了密码保护，请输入密码"}>
                <InfoCircleOutlined />
              </Tooltip>
            }
            visibilityToggle={{
              visible: showPassword,
              onVisibleChange: handlePasswordVisibilityToggle,
            }}
            status={backupMetadata && backupMetadata.hasPasswordProtection && !customPassword ? "warning" : ""}
            allowClear
          />
          {backupMetadata && backupMetadata.hasPasswordProtection && (
            <Alert
              message={
                <Text type="warning" style={{ fontSize: '12px' }}>
                  <InfoCircleOutlined style={{ marginRight: '8px' }} />
                  此备份已启用密码保护，恢复时必须提供正确的密码
                </Text>
              }
              type="warning"
              showIcon={false}
              style={{ 
                marginTop: '8px', 
                padding: '6px 12px',
                background: 'rgba(250, 173, 20, 0.05)', 
                border: '1px solid rgba(250, 173, 20, 0.1)',
                borderRadius: '4px'
              }}
            />
          )}
        </div>
        
        <Button
          type="primary"
          icon={<CloudDownloadOutlined />}
          onClick={handleRestoreBackup}
          loading={restoreLoading}
          disabled={!backupId || !verificationCode || !backupMetadata || (backupMetadata && backupMetadata.hasPasswordProtection && !customPassword)}
          className="primary-gradient-btn"
          style={{ marginTop: 16, width: '100%', height: 44 }}
        >
          恢复备份
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <FileProtectOutlined style={{ fontSize: '20px' }} />
          云端备份与恢复
        </span>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={550}
      className="cloud-backup-modal"
      destroyOnClose
      closeIcon={<CloseOutlined style={{ fontSize: '16px' }} />}
    >
      <Tabs 
        activeKey={activeTab} 
        onChange={setActiveTab}
        centered
        items={[
          {
            key: "backup",
            label: (
              <span>
                <CloudUploadOutlined />
                备份
              </span>
            ),
            children: renderBackupTab()
          },
          {
            key: "restore",
            label: (
              <span>
                <CloudDownloadOutlined />
                恢复
              </span>
            ),
            children: renderRestoreTab()
          }
        ]}
      />
    </Modal>
  );
};

export default CloudBackup;
